/**
 * Player profile page showing ELO, advanced stats, recent matches, and heatmaps.
 * Fetches data from multiple API endpoints using the player's steamid64.
 *
 * The content is organized into four tabs (overview / statistics /
 * weapons & maps / achievements) so the page stays scannable; only the
 * active tab is mounted. The active tab is mirrored in the `?tab=` query
 * param so links and the back button keep the selection.
 */
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../../i18n';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_URL } from '../../utils/api';
import { getMedalImageUrl } from '../../utils/formatters';
import { flairNameProps, flairPlateProps } from '../../utils/flair';
import { fetchAvatarsBulk } from '../../utils/avatars';
import FlairEmblem from '../shop/FlairEmblem';
import FlairInventory from '../shop/FlairInventory';
import StatCard from '../common/StatCard';
import ImageLightbox from '../common/ImageLightbox';
import './PlayerProfile.css';
import SteamAvatar from '../common/SteamAvatar';
import ProgressionCharts from '../stats/ProgressionCharts';
import '../highlights/Highlights.css';
import Achievements from '../achievements/Achievements';
import EloProfileSection from './EloProfileSection';
import LeaguesProfileSection from './LeaguesProfileSection';
import {
  PlaystyleSection, CoreStatsTab, WeaponsMapsTab, HighlightsSection,
} from './PlayerStatsSections';
import PlayerPredictions from './PlayerPredictions';
import RecentMatchesSection from './RecentMatchesSection';
import WeeklyRecapCard from './WeeklyRecapCard';
import RegionBadge from '../leagues/RegionBadge';
import CountrySelect from '../leagues/CountrySelect';
import { useAuth } from '../../contexts/AuthContext';
import usePermissions from '../../hooks/usePermissions';

// Tab ids in display order; "overview" is the default and kept out of the URL.
const PROFILE_TABS = ['overview', 'stats', 'weapons', 'leagues', 'awards'];

// Inventoryt är bara ditt eget: det visar vad du äger och låter dig byta, så
// det hör inte hemma på någon annans profil. Samma regel som landsknappen i
// profilhuvudet. Att lägga det här (och inte under /profile) är avsiktligt —
// profilhuvudet ovanför ÄR det flairen ändrar, så bytet syns direkt.
const OWN_PROFILE_TABS = [...PROFILE_TABS, 'inventory'];

const TAB_LABEL_KEYS = {
  overview: 'profile.tabOverview',
  stats: 'profile.tabStats',
  weapons: 'profile.tabWeapons',
  leagues: 'nav.leagues',
  awards: 'profile.tabAwards',
  inventory: 'inventory.tab',
};

const PlayerProfile = ({ steamid64, onBack, onSelectMatch, onSelectH2H }) => {
  const { t } = useTranslation();
  const { user, getAuthHeaders, isLoggedIn } = useAuth();
  const { features } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [player, setPlayer] = useState(null);
  const [country, setCountry] = useState('');
  const [editCountry, setEditCountry] = useState(false);
  const [countryDraft, setCountryDraft] = useState('');
  const [savingCountry, setSavingCountry] = useState(false);
  const [eloData, setEloData] = useState(null);
  const [advancedStats, setAdvancedStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [highlights, setHighlights] = useState([]);
  const [medals, setMedals] = useState([]);
  const [weaponStats, setWeaponStats] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  const [allPlayers, setAllPlayers] = useState([]);
  const [compareSearch, setCompareSearch] = useState('');
  const [lightboxMedal, setLightboxMedal] = useState(null);
  const rootRef = useRef(null);
  const tabsRef = useRef(null);
  const closeMedalLightbox = useCallback(() => setLightboxMedal(null), []);

  // The viewer owns this profile when their linked Steam id matches it.
  const isOwnProfile = isLoggedIn && user?.steam_id && String(user.steam_id) === String(steamid64);

  // Samma flagga som gatar butiksrutten. Med butiken släckt äger ingen flair,
  // så fliken vore tom — och dess länk till /shop skulle peka på en route som
  // inte finns. `!== false` speglar App.js: okänd flagga = på.
  const showInventory = isOwnProfile && features.SHOP_ENABLED !== false;
  const tabs = showInventory ? OWN_PROFILE_TABS : PROFILE_TABS;
  const tabParam = searchParams.get('tab');
  // Validerar mot den synliga listan, inte mot alla tänkbara flikar: ?tab=inventory
  // på någon annans profil faller därför tillbaka på overview i stället för att
  // rendera en panel som inte hör hemma där.
  const activeTab = tabs.includes(tabParam) ? tabParam : 'overview';

  /**
   * Switch profile tab: mirror the choice in the URL (replace, so tab
   * flips don't pollute history) and, when the sticky tab bar is pinned
   * under the app header, scroll back up so the new tab starts at its top.
   */
  const selectTab = useCallback((tab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'overview') next.delete('tab');
      else next.set('tab', tab);
      return next;
    }, { replace: true });
    const tabsEl = tabsRef.current;
    const headerHeight = document.querySelector('.header')?.offsetHeight || 0;
    if (tabsEl && tabsEl.getBoundingClientRect().top <= headerHeight + 1) {
      tabsEl.scrollIntoView();
    }
  }, [setSearchParams]);

  // The sticky tab bar must pin just below the app header, whose height
  // varies with viewport/nav wrapping — measure it into a CSS custom
  // property that PlayerProfile.css reads for `top`/`scroll-margin-top`.
  useEffect(() => {
    const header = document.querySelector('.header');
    const root = rootRef.current;
    if (!header || !root) return undefined;
    const apply = () => root.style.setProperty('--app-header-h', `${header.offsetHeight}px`);
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(header);
    return () => observer.disconnect();
  }, [loading]);

  const saveCountry = useCallback(async (code) => {
    setSavingCountry(true);
    try {
      const r = await fetch(`${API_URL}/api/player/${steamid64}/country`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ country: code }),
      });
      if (r.ok) {
        const data = await r.json().catch(() => ({}));
        setCountry(data.country || '');
        setEditCountry(false);
      }
    } finally {
      setSavingCountry(false);
    }
  }, [steamid64, getAuthHeaders]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_URL}/api/player/${steamid64}`)
      .then(res => {
        if (!res.ok) throw new Error('Player not found');
        return res.json();
      })
      .then(async (data) => {
        await fetchAvatarsBulk([data.steamid64]);
        setPlayer(data);
        setCountry(data.country || '');
        setEditCountry(false);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [steamid64]);

  useEffect(() => {
    fetch(`${API_URL}/api/elo/player/${steamid64}`)
      .then(res => res.json())
      .then(data => {
        if (!data.error) {
          setEloData(data);
        }
      })
      .catch(() => {});
  }, [steamid64]);

  /**
   * Load advanced stats, which also carry the equipped flair shown in the
   * header. Callable so the inventory can refresh the header after an equip —
   * seeing the change land on your own name is the whole reason the inventory
   * lives on this page.
   */
  const loadAdvancedStats = useCallback(() => {
    fetch(`${API_URL}/api/stats/player/${steamid64}`)
      .then(res => res.json())
      .then(data => {
        if (!data.error) {
          setAdvancedStats(data);
        }
      })
      .catch(() => {});
  }, [steamid64]);

  useEffect(() => { loadAdvancedStats(); }, [loadAdvancedStats]);

  useEffect(() => {
    fetch(`${API_URL}/api/highlights/player/${steamid64}`)
      .then(res => res.json())
      .then(data => setHighlights(data.highlights || []))
      .catch(() => setHighlights([]));
  }, [steamid64]);

  useEffect(() => {
    fetch(`${API_URL}/api/tournaments/medals/player/${steamid64}`)
      .then(res => res.ok ? res.json() : [])
      .then(data => setMedals(Array.isArray(data) ? data : []))
      .catch(() => setMedals([]));
  }, [steamid64]);

  useEffect(() => {
    fetch(`${API_URL}/api/stats/player/${steamid64}/weapons`)
      .then(res => res.json())
      .then(data => {
        if (!data.error && data.weapons) {
          setWeaponStats(data);
        }
      })
      .catch(() => {});
  }, [steamid64]);

  if (loading) return <div className="loading-screen"><div className="loader"></div></div>;
  if (error) return (
    <div className="error-screen">
      <button className="back-button" onClick={onBack}>← {t('profile.back')}</button>
      <p>{t('profile.error')}: {error}</p>
    </div>
  );
  if (!player) return (
    <div className="error-screen">
      <button className="back-button" onClick={onBack}>← {t('profile.back')}</button>
      <p>{t('profile.notFound')}</p>
    </div>
  );

  return (
    <div className="player-profile" ref={rootRef}>
      <button className="back-button" onClick={onBack}>← {t('profile.back')}</button>

      <div className="profile-header lift-card notched-card tactical-grid">
        <SteamAvatar steamid64={player.steamid64} name={player.name} size="large" />
        {/* Profilen är enda stället där emblemet får sin fulla storlek —
            detaljerna som försvinner i topplistans 32 px syns bara här. */}
        <FlairEmblem
          emblem={advancedStats?.flair?.emblem}
          title={t('shop.title')}
          className="flair-emblem--profile"
        />
        <div className="profile-info">
          <h1>
            {country && <RegionBadge region={country} className="profile-flag" />}
            <span {...flairNameProps(advancedStats?.flair?.name_color, 'profile-name')}>
              {player.name}
            </span>
            {advancedStats?.flair?.badge && (
              <span className="profile-flair-badge" title={t('shop.title')}>
                {advancedStats.flair.badge}
              </span>
            )}
          </h1>
          {advancedStats?.flair?.title && (
            <span
              {...flairPlateProps(
                advancedStats.flair.title_style,
                advancedStats.flair.name_color,
                'profile-flair-title',
              )}
            >
              {/* Utan platta ramar hakparenteserna in titeln; med platta är
                  ramen redan plattans jobb. */}
              {advancedStats.flair.title_style
                ? advancedStats.flair.title
                : `「${advancedStats.flair.title}」`}
            </span>
          )}
          <span className="steam-id">Steam ID: {player.steamid64}</span>
          {isOwnProfile && !editCountry && (
            <button
              className="profile-country-edit"
              onClick={() => { setCountryDraft(country); setEditCountry(true); }}
            >
              {country ? t('profile.changeCountry') : t('profile.setCountry')}
            </button>
          )}
          {isOwnProfile && editCountry && (
            <div className="profile-country-editor">
              <CountrySelect value={countryDraft} onChange={setCountryDraft} />
              <button className="btn-primary-sm" disabled={savingCountry} onClick={() => saveCountry(countryDraft)}>
                Spara
              </button>
              <button className="btn-subtle" disabled={savingCountry} onClick={() => setEditCountry(false)}>
                Avbryt
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="stats-overview">
        <StatCard label={t('profile.kdRatio')} value={player.overview.kd_ratio} icon="⚔️" index={0} />
        <StatCard label={t('profile.headshotPct')} value={`${player.overview.headshot_percentage}%`} icon="🎯" index={1} />
        <StatCard label="ADR" value={player.overview.avg_adr} icon="💥" index={2} />
        <StatCard label={t('players.colMatches')} value={player.overview.total_matches} icon="🎮" index={3} />
      </div>

      {onSelectH2H && (
        <div className="compare-section">
          <button
            className={`compare-btn-trigger ${showCompare ? 'active' : ''}`}
            onClick={() => {
              if (!showCompare && allPlayers.length === 0) {
                fetch(`${API_URL}/api/players`)
                  .then(r => r.json())
                  .then(async (data) => {
                    const list = (data.players || []).filter(p => p.steamid64 !== steamid64);
                    await fetchAvatarsBulk(list.map(p => p.steamid64));
                    setAllPlayers(list);
                  })
                  .catch(() => {});
              }
              setShowCompare(!showCompare);
              setCompareSearch('');
            }}
          >
            {showCompare ? t('profile.close') : t('profile.compareWith')}
          </button>
          {showCompare && (
            <div className="compare-dropdown">
              <input
                className="compare-search"
                type="text"
                placeholder={t('players.searchPlaceholder')}
                value={compareSearch}
                onChange={(e) => setCompareSearch(e.target.value)}
                autoFocus
              />
              <div className="compare-player-list">
                {allPlayers
                  .filter(p => p.name.toLowerCase().includes(compareSearch.toLowerCase()))
                  .slice(0, 10)
                  .map(p => (
                    <div
                      key={p.steamid64}
                      className="compare-player-item"
                      onClick={() => onSelectH2H(steamid64, p.steamid64)}
                    >
                      <SteamAvatar steamid64={p.steamid64} name={p.name} size="small" />
                      <span>{p.name}</span>
                    </div>
                  ))}
                {allPlayers.length === 0 && <div className="compare-loading">{t('profile.loadingPlayers')}</div>}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="profile-tabs" ref={tabsRef} role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab}
            role="tab"
            aria-selected={activeTab === tab}
            className={`profile-tab ${activeTab === tab ? 'is-active' : ''}`}
            onClick={() => selectTab(tab)}
          >
            {t(TAB_LABEL_KEYS[tab])}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="detailed-stats profile-tab-panel">
          <EloProfileSection eloData={eloData} />
          <WeeklyRecapCard steamid64={steamid64} />
          <PlaystyleSection advancedStats={advancedStats} />
          {player.recent_matches && player.recent_matches.length >= 3 && (
            <ProgressionCharts recentMatches={player.recent_matches} />
          )}
          <RecentMatchesSection matches={player.recent_matches} onSelectMatch={onSelectMatch} />
        </div>
      )}

      {activeTab === 'stats' && (
        <div className="detailed-stats profile-tab-panel">
          <CoreStatsTab player={player} advancedStats={advancedStats} />
        </div>
      )}

      {activeTab === 'weapons' && (
        <div className="detailed-stats profile-tab-panel">
          <WeaponsMapsTab
            player={player}
            steamid64={steamid64}
            advancedStats={advancedStats}
            weaponStats={weaponStats}
          />
        </div>
      )}

      {activeTab === 'leagues' && (
        <div className="detailed-stats profile-tab-panel">
          <LeaguesProfileSection steamid64={steamid64} showEmpty />
        </div>
      )}

      {/* Villkoret styr redan fliklistan; det upprepas här så att en direktlänk
          aldrig kan rendera panelen på någon annans profil. */}
      {activeTab === 'inventory' && showInventory && (
        <div className="detailed-stats profile-tab-panel">
          <FlairInventory onChange={loadAdvancedStats} />
        </div>
      )}

      {activeTab === 'awards' && (
        <div className="detailed-stats profile-tab-panel">
          <Achievements steamid64={steamid64} />

          {medals.length > 0 && (
            <div className="medal-showcase">
              <h2 className="section-title section-heading">{t('profile.tournamentMedals')}</h2>
              <div className="medal-grid">
                {medals.map((m) => {
                  const tierClass = m.placement === 1 ? 'gold' : m.placement === 2 ? 'silver' : 'bronze';
                  return (
                    <div
                      key={m.id}
                      className={`medal-card ${tierClass} clickable`}
                      onClick={() => setLightboxMedal(m)}
                    >
                      <img
                        src={getMedalImageUrl(m.image_url)}
                        alt={`${m.tournament_name} #${m.placement}`}
                        className="medal-card-img"
                      />
                      <div className="medal-card-info">
                        <span className="medal-card-tournament">{m.tournament_name}</span>
                        <span className="medal-card-placement">
                          {m.placement === 1 ? '1st' : m.placement === 2 ? '2nd' : '3rd'} Place
                        </span>
                        <span className="medal-card-date">
                          {new Date(m.created_at).toLocaleDateString(currentLocale())}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <HighlightsSection highlights={highlights} onSelectMatch={onSelectMatch} />

          <PlayerPredictions steamid64={steamid64} onSelectMatch={onSelectMatch} />
        </div>
      )}

      {lightboxMedal && (
        <ImageLightbox
          src={getMedalImageUrl(lightboxMedal.image_url)}
          title={`${lightboxMedal.tournament_name} — ${({ 1: '1st', 2: '2nd', 3: '3rd' })[lightboxMedal.placement]} Place`}
          subtitle={new Date(lightboxMedal.created_at).toLocaleDateString(currentLocale())}
          onClose={closeMedalLightbox}
        />
      )}
    </div>
  );
};

export default PlayerProfile;
